import { useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CaretRight, ChatCircle, DotsThree, Plus } from '@phosphor-icons/react';
import type { StaffDetail } from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { RowMenu } from '../../../../../components/directory/row-menu';
import { staffMenuItems } from '../../../../../components/staff/staff-menu';
import { IdentityHeader, IdentityTile } from '../../../../../components/shell/identity-header';
import { UnderlineTabs } from '../../../../../components/ui/underline-tabs';
import { textLinkClass } from '../../../../../components/ui/text-link';
import { EditStaffDialog } from '../../../../../components/staff/edit-staff-dialog';
import { SuspendStaffDialog } from '../../../../../components/staff/suspend-staff-dialog';
import { Icon } from '../../../../../components/Icon';
import { Banner } from '../../../../../components/ui/banner';
import { Card } from '../../../../../components/ui/card';
import { PageSkeleton } from '../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../components/ui/state-views';
import { Chip } from '../../../../../components/ui/chip';
import { StatusTag } from '../../../../../components/ui/status-tag';
import { useToast } from '../../../../../components/ui/toast';
import { pillClass } from '../../../../../components/ui/toolbar-controls';
import { cx } from '../../../../../lib/cx';
import { staffQuery, useReinstateStaff } from '../../../../../lib/directory-api';
import { describeActivity, lastActiveLabel, roleLabel, roleSubtitle } from '../../../../../lib/directory-display';
import { requireAdmin } from '../../../../../lib/guards';
import { initialsOf } from '../../../../../lib/initials';
import { formatDayYear } from '../../../../../lib/project-display';
import { useSession } from '../../../../../lib/session';
import { weekdayDay } from '../../../../../lib/task-display';
import { toasts } from '../../../../../lib/toast-presets';

export const Route = createFileRoute('/subdomain/admin/_staff/staff/$staffId')({
  beforeLoad: ({ context }) => requireAdmin(context.queryClient),
  // The page is 1200 wide in the design (same canvas as the tables).
  staticData: { wide: true },
  component: StaffProfilePage,
});

const HEAD = 'text-label-small text-text-tertiary uppercase';
const monthYear = new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** A table inside a card (Figma A04.2): a grey head bar, then 52px rows split by hairlines. */
const HEAD_ROW =
  'grid h-(--mbm-size-table-head-sm) items-center rounded-(--mbm-component-radius-rail-item) bg-surface-canvas';
const BODY_ROW =
  'grid min-h-(--mbm-size-table-row-sm) items-center border-t border-border-line hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';
const CLIENT_COLS =
  'grid-cols-[minmax(0,220fr)_minmax(0,150fr)_minmax(0,110fr)_minmax(0,130fr)_minmax(0,110fr)_minmax(0,40fr)]';
const PROJECT_COLS =
  'grid-cols-[minmax(0,240fr)_minmax(0,150fr)_minmax(0,110fr)_minmax(0,130fr)_minmax(0,130fr)_minmax(0,40fr)]';

const CARD_BODY = 'flex flex-col gap-(--mbm-space-3) px-(--mbm-space-5) pb-(--mbm-space-3) pt-(--mbm-space-5)';

function Fact({ label, children }: { label: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-0-5)">
      <dt className="text-caption text-text-tertiary">{label}</dt>
      <dd className="text-body-medium text-text-primary">{children || '—'}</dd>
    </div>
  );
}

function Stat({ label, value, warn = false }: { label: string; value: ReactNode; warn?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-(--mbm-space-4)">
      <dt className="text-body-small text-text-secondary">{label}</dt>
      <dd className={cx('text-label-medium', warn ? 'text-feedback-warning' : 'text-text-primary')}>{value}</dd>
    </div>
  );
}

function CardHead({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-(--mbm-space-3)">
      <div className="flex flex-col gap-(--mbm-space-0-5)">
        <h2 className="text-heading-h4 text-text-strong">{title}</h2>
        {sub ? <p className="text-body-xsmall text-text-tertiary">{sub}</p> : null}
      </div>
      {action}
    </div>
  );
}

/** What an open task list says per project: the business, the project, how many are open and the soonest due. */
function projectsOf(tasks: StaffDetail['openTasks']) {
  const byKey = new Map<string, { business: string; project: string; open: number; due: string | null }>();
  for (const task of tasks) {
    const key = `${task.businessName}|${task.projectName}`;
    const row = byKey.get(key) ?? { business: task.businessName ?? '—', project: task.projectName, open: 0, due: null };
    row.open += 1;
    if (task.deadline && (!row.due || task.deadline < row.due)) row.due = task.deadline;
    byKey.set(key, row);
  }
  return [...byKey.values()];
}

/**
 * A person on the team (Figma A04.2): who they are, the clients they manage (an account manager) or the projects they
 * work on (a lead or specialist), this week's load, their profile, and what they did lately.
 * TODO(api): the design's weekly figures (drafts to publish, meetings, reply time, revision rate), the plan column,
 * their phone number, and the Clients, Work, Capacity, Chat and Activity tabs need scheduling, chat, billing and
 * dispatch that do not exist yet; they are drawn as designed and inert. "Message", "Assign clients" and "Assign work"
 * wait for chat and the Task intake filter.
 */
function StaffProfilePage() {
  const { staffId } = Route.useParams();
  const toast = useToast();
  const { data: session } = useSession();
  const { data: person, isLoading, error, refetch } = useQuery(staffQuery(staffId));
  const [editing, setEditing] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const reinstate = useReinstateStaff(staffId);
  const soon =
    (feature = 'This') =>
    () =>
      toast(toasts.comingSoon(`${feature} arrives with its own sprint.`));

  if (isLoading) return <PageSkeleton />;

  if (!person) {
    return (
      <LoadFailure
        error={error}
        noun="person"
        forbiddenHint="Ask an admin."
        notFoundHint="They may have been removed."
        action={
          <Link
            to="/subdomain/admin/staff"
            search={{ tab: undefined, q: undefined }}
            className="text-label-large text-brand-primary"
          >
            Back to staff
          </Link>
        }
        onRetry={() => void refetch()}
      />
    );
  }

  const suspended = person.status === 'suspended';
  const isSelf = person.id === session?.user.id;
  const subtitle = roleSubtitle(person);
  const holdsClients = person.staffRole === 'account_manager' || person.clients.length > 0;
  const backupCount = person.clientCount - person.primaryClientCount;
  const projects = projectsOf(person.openTasks);
  const load = person.maxActiveTasks > 0 ? Math.round((person.activeTaskCount / person.maxActiveTasks) * 100) : 0;
  const over = load > 100;

  const lift = async () => {
    try {
      await reinstate.mutateAsync();
      toast({ title: `${person.name} has access again`, description: 'Their old assignments weren’t restored.' });
    } catch (err) {
      toast({
        title: 'Couldn’t reinstate',
        description: (err as Partial<MappedApiError>).message ?? 'Please try again.',
        tone: 'error',
      });
    }
  };

  const menu = staffMenuItems(person, isSelf, {
    onEdit: () => setEditing(true),
    onSuspend: () => setSuspending(true),
    onReinstate: () => void lift(),
    onSoon: (feature) => soon(feature)(),
  });

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-(--mbm-space-1-5) text-label-medium text-text-secondary"
      >
        <Link
          to="/subdomain/admin/staff"
          search={{ tab: undefined, q: undefined }}
          className="inline-flex items-center gap-(--mbm-space-1-5) hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={ArrowLeft} size="sm" />
          Staff
        </Link>
        <span aria-hidden className="font-normal text-text-tertiary">
          /
        </span>
        <span className="text-text-primary">{person.name}</span>
      </nav>

      <IdentityHeader
        tile={<IdentityTile shape="disc">{initialsOf(person.name)}</IdentityTile>}
        title={person.name}
        meta={
          <>
            <StatusTag
              size="md"
              label={suspended ? 'Suspended' : 'Active'}
              tone={suspended ? 'grey' : 'green'}
              variant={suspended ? 'outline' : 'soft'}
            />
            <Chip size="md">{roleLabel(person.staffRole)}</Chip>
            <span>
              {[subtitle, person.email, `last active ${lastActiveLabel(person.lastActiveAt).toLowerCase()}`]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        }
        actions={
          <>
            <button type="button" onClick={soon('Messaging')} className={pillClass('light', 'lg')}>
              <Icon icon={ChatCircle} size="sm" />
              Message
            </button>
            <button
              type="button"
              onClick={soon(holdsClients ? 'Assigning clients' : 'Assigning work')}
              className={pillClass('dark', 'lg')}
            >
              <Icon icon={Plus} size="sm" />
              {holdsClients ? 'Assign clients' : 'Assign work'}
            </button>
            <RowMenu round label={`Actions for ${person.name}`} items={menu} />
          </>
        }
      />

      <UnderlineTabs
        aria-label="Person sections"
        value="overview"
        onChange={() => {}}
        onUnavailable={(tab) => soon(tab.label)()}
        tabs={[
          { value: 'overview', label: 'Overview' },
          holdsClients
            ? { value: 'clients', label: 'Clients', count: person.clientCount, unavailable: true }
            : { value: 'projects', label: 'Projects', count: projects.length, unavailable: true },
          { value: 'work', label: 'Work', count: person.activeTaskCount, unavailable: true },
          { value: 'capacity', label: 'Capacity', unavailable: true },
          { value: 'chat', label: 'Chat', unavailable: true },
          { value: 'activity', label: 'Activity', unavailable: true },
        ]}
      />

      {suspended ? (
        <Banner tone="info" title="Access is suspended">
          They’re signed out and can’t sign in until you reinstate them.
        </Banner>
      ) : null}

      <div className="grid items-start gap-(--mbm-space-5) lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)]">
        <div className="flex flex-col gap-(--mbm-space-5)">
          {holdsClients ? (
            <Card tone="panel" padding="none" className={CARD_BODY}>
              <CardHead
                title="Clients they manage"
                sub={`${person.clientCount} ${person.clientCount === 1 ? 'business' : 'businesses'} · primary on ${person.primaryClientCount} · backup on ${backupCount}`}
                action={
                  <button
                    type="button"
                    onClick={soon('The client list')}
                    className={cx(textLinkClass, 'inline-flex items-center gap-(--mbm-space-1)')}
                  >
                    All {person.clientCount} clients
                    <Icon icon={CaretRight} size="xs" />
                  </button>
                }
              />
              {person.clients.length === 0 ? (
                <p className="pb-(--mbm-space-2) text-body-medium text-text-secondary">
                  No clients yet. Assign some from Businesses.
                </p>
              ) : (
                <div className="flex flex-col pb-(--mbm-space-2)">
                  <div className={cx(HEAD_ROW, CLIENT_COLS)}>
                    <span className={cx(HEAD, 'pl-(--mbm-space-3)')}>Business</span>
                    <span className={HEAD}>Plan</span>
                    <span className={HEAD}>Their role</span>
                    <span className={HEAD}>Open work</span>
                    <span className={HEAD}>Since</span>
                    <span aria-hidden />
                  </div>
                  {person.clients.map((client) => (
                    <Link
                      key={client.id}
                      to="/subdomain/admin/businesses/$businessId"
                      params={{ businessId: client.id }}
                      className={cx(BODY_ROW, CLIENT_COLS)}
                    >
                      <span className="flex min-w-0 items-center gap-(--mbm-space-2-5) pl-(--mbm-space-3)">
                        <span
                          aria-hidden
                          className="flex size-(--mbm-size-avatar-xs) shrink-0 items-center justify-center rounded-(--mbm-component-radius-table-avatar) bg-surface-field text-label-small text-text-primary"
                        >
                          {initialsOf(client.name)}
                        </span>
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-label-medium text-text-primary">{client.name}</span>
                          {client.city ? (
                            <span className="truncate text-body-xsmall text-text-tertiary">{client.city}</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="text-body-small text-text-tertiary">—</span>
                      <span>
                        <StatusTag
                          size="md"
                          label={client.isPrimary ? 'Primary' : 'Backup'}
                          tone="blue"
                          variant={client.isPrimary ? 'soft' : 'outline'}
                        />
                      </span>
                      <span className="text-body-small text-text-primary">{client.openTaskCount} open</span>
                      <span className="text-body-small text-text-primary">
                        {monthYear.format(new Date(client.since))}
                      </span>
                      <span aria-hidden className="text-text-muted">
                        <Icon icon={DotsThree} size="md" />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          ) : (
            <Card tone="panel" padding="none" className={CARD_BODY}>
              <CardHead
                title="Projects they work on"
                sub={`From their task assignments · ${person.activeTaskCount} open ${person.activeTaskCount === 1 ? 'task' : 'tasks'}`}
              />
              {projects.length === 0 ? (
                <p className="pb-(--mbm-space-2) text-body-medium text-text-secondary">
                  No tasks assigned to them right now.
                </p>
              ) : (
                <div className="flex flex-col">
                  <div className={cx(HEAD_ROW, PROJECT_COLS)}>
                    <span className={cx(HEAD, 'pl-(--mbm-space-3)')}>Business</span>
                    <span className={HEAD}>Project</span>
                    <span className={HEAD}>Their role</span>
                    <span className={HEAD}>Open work</span>
                    <span className={HEAD}>Next due</span>
                    <span aria-hidden />
                  </div>
                  {projects.map((row) => (
                    <div key={`${row.business}|${row.project}`} className={cx(BODY_ROW, PROJECT_COLS)}>
                      <span className="flex min-w-0 items-center gap-(--mbm-space-2-5) pl-(--mbm-space-3)">
                        <span
                          aria-hidden
                          className="flex size-(--mbm-size-avatar-xs) shrink-0 items-center justify-center rounded-(--mbm-component-radius-table-avatar) bg-surface-field text-label-small text-text-primary"
                        >
                          {initialsOf(row.business)}
                        </span>
                        <span className="truncate text-label-medium text-text-primary">{row.business}</span>
                      </span>
                      <span className="truncate text-body-small text-text-primary">{row.project}</span>
                      <span>
                        <StatusTag
                          size="md"
                          label={person.staffRole === 'lead_specialist' ? 'Lead' : 'Specialist'}
                          tone="blue"
                          variant={person.staffRole === 'lead_specialist' ? 'soft' : 'outline'}
                        />
                      </span>
                      <span className="text-body-small text-text-primary">{row.open} open</span>
                      <span className="text-body-small text-text-primary">{row.due ? weekdayDay(row.due) : '—'}</span>
                      <span aria-hidden />
                    </div>
                  ))}
                </div>
              )}
              <p className="rounded-(--mbm-radius-sm) bg-surface-canvas px-(--mbm-space-3) py-(--mbm-space-2-5) text-caption text-text-secondary">
                People join a project when they get a task in it. To give {person.name.split(' ')[0]} more work, use
                Assign work, which opens Task intake filtered to their skills.
              </p>
            </Card>
          )}

          <Card
            tone="panel"
            padding="none"
            className="flex flex-col gap-(--mbm-space-3) px-(--mbm-space-5) pb-(--mbm-space-4) pt-(--mbm-space-5)"
          >
            <CardHead
              title="Recent activity"
              action={
                <button type="button" onClick={soon('The audit log')} className={textLinkClass}>
                  Full audit log
                </button>
              }
            />
            {person.recentActivity.length === 0 ? (
              <p className="text-body-medium text-text-secondary">Nothing recorded yet.</p>
            ) : (
              <ul className="flex flex-col">
                {person.recentActivity.map((entry) => (
                  <li
                    key={entry.id}
                    className="grid grid-cols-[8rem_minmax(0,1fr)] items-baseline gap-(--mbm-space-4) py-(--mbm-space-2)"
                  >
                    <span className="text-body-xsmall text-text-tertiary">{lastActiveLabel(entry.createdAt)}</span>
                    <span className="text-body-medium text-text-primary">{describeActivity(entry.action)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-(--mbm-space-5)">
          <Card
            tone="panel"
            padding="none"
            className="flex flex-col gap-(--mbm-space-3-5) px-(--mbm-space-5) pb-(--mbm-space-5) pt-(--mbm-space-5)"
          >
            <h2 className="text-heading-h4 text-text-strong">This week</h2>
            <p className="flex items-end gap-(--mbm-space-2)">
              <span className={cx('text-metric-xl', over ? 'text-feedback-warning' : 'text-text-strong')}>{load}%</span>
              <span className="pb-(--mbm-space-1-5) text-body-small text-text-secondary">
                of their task capacity
                {over ? ` · over by ${person.activeTaskCount - person.maxActiveTasks}` : ''}
              </span>
            </p>
            <div
              role="progressbar"
              aria-label="Task capacity used"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(load, 100)}
              className="h-(--mbm-space-1-5) w-full overflow-hidden rounded-full bg-surface-field"
            >
              <div
                className={cx(
                  'h-full rounded-full',
                  over || load === 100 ? 'bg-feedback-warning' : 'bg-action-primary',
                )}
                style={{ width: `${Math.min(load, 100)}%` }}
              />
            </div>
            <dl className="flex flex-col gap-(--mbm-space-3)">
              <Stat
                label="Active tasks"
                value={`${person.activeTaskCount} of ${person.maxActiveTasks} max`}
                warn={over}
              />
              {holdsClients ? (
                <Stat label="Clients" value={`${person.clientCount} · ${person.primaryClientCount} primary`} />
              ) : (
                <Stat label="Pod" value={person.department ?? '—'} />
              )}
              <Stat label={holdsClients ? 'Drafts to publish' : 'Drafts to QA'} value="—" />
              <Stat label={holdsClients ? 'Meetings this week' : 'Revision rate (30 days)'} value="—" />
              {holdsClients ? <Stat label="Avg chat reply" value="—" /> : null}
            </dl>
          </Card>

          <Card
            tone="panel"
            padding="none"
            className="flex flex-col gap-(--mbm-space-4) px-(--mbm-space-5) pb-(--mbm-space-5) pt-(--mbm-space-5)"
          >
            <CardHead
              title="Profile"
              action={
                <button type="button" onClick={() => setEditing(true)} className={textLinkClass}>
                  Edit
                </button>
              }
            />
            <dl className="flex flex-col gap-(--mbm-space-3-5)">
              <Fact label="Department · Title">{[person.department, person.title].filter(Boolean).join(' · ')}</Fact>
              <Fact label="Skills">{person.skills.join(', ')}</Fact>
              <Fact label="Max active tasks">{person.maxActiveTasks}</Fact>
              <Fact label="Joined">{formatDayYear(person.createdAt)}</Fact>
            </dl>
          </Card>
        </div>
      </div>

      <EditStaffDialog person={editing ? person : null} onClose={() => setEditing(false)} />
      <SuspendStaffDialog person={suspending ? person : null} onClose={() => setSuspending(false)} />
    </>
  );
}
